import React, { useRef, useState } from "react";

import { api } from "network/network";
import { API_HOST_WMS } from "config/api.config";
import { APIendpoints } from "utils/constants/APIendpoints";
import _get from "lodash-es/get";
import WhiltelistedFileTypes from "../../Common/Logout";
import { ONBOARDING_DOCUMENT_FILE_TYPE } from "utils/constants/onboarding";
import styles from "../Form.module.scss";

const MAX_FILE_BYTES = 10 * 1024 * 1024;

// admin-api's allowedFileExtensions already accepts these; exposing them here for document substeps.
const EXTRA_ALLOWED_TYPES = new Set([
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

const DEFAULT_ACCEPT = "image/jpeg,image/jpg,image/png,image/tiff,application/pdf,.csv,.xls,.xlsx,.doc,.docx";
const DEFAULT_ERROR_MSG = "Only JPG, PNG, PDF, CSV, Excel or Word files are allowed.";

// Build accept string and validator from allowedExtensions prop (e.g. ['image/*', '.pdf'])
// If not provided, falls back to the original broad allowlist.
const buildAcceptAndValidator = (allowedExtensions) => {
  if (!allowedExtensions || allowedExtensions.length === 0) {
    return {
      accept: DEFAULT_ACCEPT,
      errorMsg: DEFAULT_ERROR_MSG,
      isAllowed: (file) =>
        WhiltelistedFileTypes.WhiltelistedFileTypes(file.type) ||
        WhiltelistedFileTypes.WhiltelistedFileTypesForDocuments(file.type) ||
        EXTRA_ALLOWED_TYPES.has(file.type) ||
        /\.csv$/i.test(file.name),
    };
  }

  const extSet = new Set(allowedExtensions.map((e) => e.toLowerCase()));
  const acceptStr = allowedExtensions.join(",");

  const isAllowed = (file) => {
    if (extSet.has("image/*") && file.type.startsWith("image/")) return true;
    const dotExt = "." + file.name.split(".").pop().toLowerCase();
    if (extSet.has(dotExt)) return true;
    // MIME fallback for common types
    if (extSet.has(".pdf") && file.type === "application/pdf") return true;
    if (extSet.has(".csv") && (file.type === "text/csv" || /\.csv$/i.test(file.name))) return true;
    return false;
  };

  const labels = allowedExtensions
    .map((e) => (e === "image/*" ? "images" : e.replace(".", "").toUpperCase()))
    .join(", ");

  return { accept: acceptStr, errorMsg: `Only ${labels} files are allowed.`, isAllowed };
};

const NON_PREVIEWABLE_EXT = /\.(pdf|csv|xlsx?|docx?)($|\?)/i;

const openFileUrl = (url) => {
  const absoluteUrl = url.startsWith("http") || url.startsWith("blob:")
    ? url
    : `${window.location.origin}/${url.replace(/^\//, "")}`;
  if (window.openDocumentHandler) {
    window.openDocumentHandler.postMessage(
      JSON.stringify({ url: absoluteUrl, showDownload: false })
    );
  } else {
    window.open(absoluteUrl, "_blank", "noreferrer");
  }
};

const fileDisplayName = (urlOrName = "") => {
  // Try to extract filename from URL path (last segment, before query string)
  try {
    const decoded = decodeURIComponent(urlOrName.split("?")[0]);
    const segments = decoded.split("/");
    const name = segments[segments.length - 1];
    if (name) return name;
  } catch (_) {}
  return urlOrName;
};

const fileChipLabel = (urlOrName = "") => {
  const match = urlOrName.match(/\.([a-z0-9]+)($|\?)/i);
  return match ? match[1].toUpperCase() : "FILE";
};

function ImageInput({ substep, value, onChange, error, isReadOnly, allowedExtensions }) {
  const config = substep?.input_image ?? {};
  const substepId = substep?.substep_id;
  const images = value?.string_list_value ?? [];
  const previewUrls = value?._previewUrls ?? {};

  const maxImages = config.max_images > 0 ? config.max_images : 5;
  const canAddMore = images.length < maxImages;

  const { accept, errorMsg, isAllowed } = buildAcceptAndValidator(allowedExtensions);

  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [localError, setLocalError] = useState("");

  const pickFile = () => {
    if (uploading || !inputRef.current) return;
    inputRef.current.click();
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;

    setLocalError("");
    if (!isAllowed(file)) {
      setLocalError(errorMsg);
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setLocalError("File must be under 10 MB.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file, file.name);
      // Fixed literal — admin-api fileType enum is closed; substep_id already carries identity.
      formData.append("fileType", ONBOARDING_DOCUMENT_FILE_TYPE);

      const res = await api.post(API_HOST_WMS, APIendpoints.uploadDocument, formData);
      const rawPath = _get(res, ["data", "response", "response"]);
      if (!rawPath) {
        setLocalError("Upload failed. Please try again.");
        return;
      }
      // The upload API returns a presigned URL. Extract just the S3 key (path without
      // domain or query params) so the backend can download via AWS SDK using credentials.
      // e.g. "https://bucket.s3.amazonaws.com/seller_docs/file.png?X-Amz-..." → "seller_docs/file.png"
      let storedPath = rawPath;
      try {
        const parsed = new URL(rawPath.split("?")[0]);
        // decodeURIComponent converts %20→space etc. so the S3 key matches
        // what the AWS SDK expects (raw key, not URL-encoded form).
        const key = decodeURIComponent(parsed.pathname.replace(/^\//, ""));
        if (key) storedPath = key;
      } catch (_) {}
      const path = storedPath;
      const isPdfFile = /\.pdf($|\?)/i.test(rawPath);
      // Images: blob URL for immediate thumbnail display (S3 key returns 403 on private buckets).
      // PDFs: keep the original presigned URL — blob URLs aren't displayable as <img> and
      // can't be loaded cross-context in Flutter's DocumentViewerScreen.
      const viewUrl = isPdfFile ? rawPath : URL.createObjectURL(file);
      onChange(substepId, {
        string_list_value: [...images, path],
        _previewUrls: { ...previewUrls, [path]: viewUrl },
      });
    } catch (err) {
      setLocalError(_get(err, ["response", "data", "error", "message"], "Upload failed."));
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (idx) => {
    const removed = images[idx];
    const nextImages = images.filter((_, i) => i !== idx);
    const nextPreviews = { ...previewUrls };
    delete nextPreviews[removed];
    onChange(substepId, { string_list_value: nextImages, _previewUrls: nextPreviews });
  };

  const displayUrl = (path) => previewUrls[path] || path;

  return (
    <div className={styles.fieldBody}>
      <div className={styles.imageGrid}>
        {images.map((path, idx) =>
          NON_PREVIEWABLE_EXT.test(path) ? (
            <div key={idx} className={styles.fileCard}>
              <span className={styles.fileCardExt}>{fileChipLabel(path)}</span>
              <span className={styles.fileCardName}>{fileDisplayName(path)}</span>
              <button
                type="button"
                className={styles.fileCardView}
                onClick={() => openFileUrl(displayUrl(path))}
              >
                View
              </button>
              {!isReadOnly && (
                <button
                  type="button"
                  className={styles.fileCardRemove}
                  onClick={() => removeAt(idx)}
                  aria-label="Remove file"
                >
                  ×
                </button>
              )}
            </div>
          ) : (
            <div key={idx} className={styles.imageThumb}>
              <button
                type="button"
                className={styles.imageThumbBtn}
                onClick={() => openFileUrl(displayUrl(path))}
                aria-label="View image"
              >
                <img src={displayUrl(path)} alt={`upload-${idx + 1}`} />
              </button>
              {!isReadOnly && (
                <button
                  type="button"
                  className={styles.imageRemove}
                  onClick={() => removeAt(idx)}
                  aria-label="Remove file"
                >
                  ×
                </button>
              )}
            </div>
          )
        )}

        {!isReadOnly && canAddMore && (
          <button
            type="button"
            className={styles.imageAdd}
            onClick={pickFile}
            disabled={uploading}
          >
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              onChange={onFileChange}
              style={{ display: "none" }}
            />
            {uploading ? "Uploading…" : (
              <img src="/logos/upload.svg" alt="Upload" className={styles.uploadBtnIcon} />
            )}
          </button>
        )}

        {isReadOnly && images.length === 0 && (
          <span className={styles.muted}>No files uploaded</span>
        )}
      </div>

      {(error || localError) && (
        <div className={styles.fieldError}>{error || localError}</div>
      )}
    </div>
  );
}

export default ImageInput;
